import { RAIL_WIDTH_PX } from "@/components/common/bottom-nav/nav-constants";

/**
 * The narrowest the list beside a side panel may be: three 150px gallery tiles
 * plus gaps. Shared by the Photos page ([[records-view.spec]] US-006) and the
 * Issues page ([[issues-page.spec]]).
 */
export const SPLIT_MIN_LIST_WIDTH = 460;

/**
 * The side panel's fixed width at each breakpoint, widest first — like Google
 * Images' preview panel, it steps up with the window rather than taking a
 * share of it, and the list beside it takes whatever is left. Every step is
 * wider than the ~375px phone layout the panel reuses. `minWidth` is the
 * window's width.
 */
export const SIDE_PANEL_WIDTHS: ReadonlyArray<{
  minWidth: number;
  panelWidth: number;
}> = [
  { minWidth: 1536, panelWidth: 560 },
  { minWidth: 1280, panelWidth: 480 },
  { minWidth: 1024, panelWidth: 420 },
  { minWidth: 0, panelWidth: 380 },
];

/**
 * The panel width for a page of `availableWidth` (sidebar included). The steps
 * are window breakpoints, and a side panel only opens from 768px, where the
 * page sits beside the navigation rail ([[bottom-nav.spec]]) — so the window is
 * the page plus the rail. Measuring the page alone would move every step 80px
 * wider than the window it is meant for.
 */
export function sidePanelWidth(availableWidth: number): number {
  const windowWidth = availableWidth + RAIL_WIDTH_PX;
  return (
    SIDE_PANEL_WIDTHS.find((step) => windowWidth >= step.minWidth)
      ?.panelWidth ?? 380
  );
}

/**
 * How a list page (Photos, Issues) frames an open item on desktop.
 *
 * - `closed` — nothing open.
 * - `dialog` — phones: the page's own full-screen presentation.
 * - `split` — desktop: filter sidebar · list · fixed-width detail panel.
 * - `split-no-sidebar` — desktop, too tight for all three: the sidebar steps
 *   aside (filters move to a bottom sheet), list · fixed-width detail panel.
 * - `fullscreen` — desktop, too tight even for two: the item full-screen in its
 *   single-column layout.
 */
export type SidePanelLayout =
  | "closed"
  | "dialog"
  | "split"
  | "split-no-sidebar"
  | "fullscreen";

export function resolveSidePanelLayout({
  isOpen,
  isDesktop,
  availableWidth,
  sidebarWidth,
}: {
  isOpen: boolean;
  isDesktop: boolean;
  /** Width of the whole page view, sidebar included. */
  availableWidth: number;
  sidebarWidth: number;
}): SidePanelLayout {
  if (!isOpen) return "closed";
  if (!isDesktop) return "dialog";
  const listWidth = availableWidth - sidePanelWidth(availableWidth);
  if (listWidth - sidebarWidth >= SPLIT_MIN_LIST_WIDTH) return "split";
  if (listWidth >= SPLIT_MIN_LIST_WIDTH) return "split-no-sidebar";
  return "fullscreen";
}
